// HomeWidgetColorPicker — the swatch palette behind the home widget context
// menu's "Add color" / "Change color" row (2026-09-02, Nika: "on / page
// widgets, make solid - a default color for all of them. but add in context
// menu 'Add color' option so users can customize widget colors + suggested
// colors").
//
// ── Why this is not a second ContextDropdown ─────────────────────────────
// The chained-menu shape would be the cheaper build, and it is the wrong one:
// a `ContextDropdownItem` cannot carry a colour. Its ink is a two-value
// branch — `(el, s) => (s?.danger || s?.color === 'red' ? 'red' : 'title')` —
// and its only leading slots are an `Icon` painted by that same ink and an
// `Avatar` fed a `src` URL. Fourteen suggested colours rendered as fourteen
// identically-black word rows is a list of app names, not a colour picker,
// and the word rows would also be the wrong label: the user is colouring an
// arbitrary widget, so "Electric blue" is the honest name and "Studio" is a
// category error. Widening the shared row is a change in packages/shared,
// which this work does not own. So the palette is its own surface — and it
// still reuses the house chrome (`MenuPanel`, the same panel every context
// menu paints) rather than inventing a second one.
//
// ── Contract ─────────────────────────────────────────────────────────────
// Driven by `root._homeColorPicker`: `{ widgetId, x, y }`, written by
// `functions/homeContextMenu.js` when the menu row is clicked (`x`/`y` are
// the cursor coords the OPENER stamped on that row — the dispatcher has no
// event of its own). Cleared on select, on Escape, and on any outside click.
//
// Writing goes through `root._homeOps.setWidgetColor(widgetId, familyKey)` —
// the same hook bag the menu's refresh / hide / autolayout actions already
// use, so the persistence stays in pages/main.js beside the hidden-widget
// writer it mirrors, and this component owns no storage.
//
// The rows come from `el.call('homeWidgetColorOptions', root, widgetId)`, not
// from an imported table: a component that closes over a module-scope const
// reads it as undefined after `frank.toJSON` (FA201–204), which would render
// an empty palette on the platform and a full one locally.
export const HomeWidgetColorPicker = {
  scope: {
    // Viewport clamp — the same idea as ContextDropdown's `ddMenuPos`, with a
    // FIXED worst-case size instead of a measured one: this panel's content
    // is a constant 7×2 swatch grid plus two rows of chrome, so its size does
    // not depend on the data and there is nothing to settle for. Padding of 8
    // keeps it off the viewport edge.
    pickerPos(el) {
      const p = (el.state && el.state.root && el.state.root._homeColorPicker) || {}
      const pad = 8
      const vw = typeof window !== 'undefined' ? window.innerWidth : 1200
      const vh = typeof window !== 'undefined' ? window.innerHeight : 800
      const w = (el.node && el.node.offsetWidth) || 320
      const h = (el.node && el.node.offsetHeight) || 190
      return {
        left: Math.max(pad, Math.min(p.x || 0, vw - w - pad)),
        top: Math.max(pad, Math.min(p.y || 0, vh - h - pad))
      }
    },

    // Move DOM focus into the panel once it has painted, for the SAME reason
    // `functions/homeContextMenu.js:focusHomeContextMenu` exists: DOMQL's
    // keydown delegate walks the ancestor chain of `event.target`, so an
    // Escape keypress never reaches this panel's `onKeydown` unless focus is
    // inside it. Bounded poll, not one microtask check — the panel is
    // `display: none` until this render commits.
    focusPicker(el) {
      if (typeof window === 'undefined' || !el) return
      el.__hwcFocusGen = (el.__hwcFocusGen || 0) + 1
      const gen = el.__hwcFocusGen
      let ticks = 0
      const tick = () => {
        if (gen !== el.__hwcFocusGen || !el.node) return
        if (!(el.state && el.state.root && el.state.root._homeColorPicker)) return
        if (el.node.offsetWidth > 0 && el.node.offsetHeight > 0) {
          const first = el.node.querySelector('button')
          if (first && first.focus) first.focus()
          else if (el.node.focus) el.node.focus()
          return
        }
        if (++ticks < 10) window.setTimeout(tick, 20)
      }
      tick()
    },

    closePicker(s) {
      if (s && s.root) s.root.update({ _homeColorPicker: null }, { preventFetch: true })
    },

    // The "App color" row's data, or null when this widget declares no
    // manifest hue (or declares one that does not resolve). Resolved through
    // the registered function rather than inline so the swatch, the tick and
    // the click handler all read ONE derivation — and so nothing here closes
    // over a module-scope table (FA201–204).
    appOption(el, s) {
      const p = s && s.root && s.root._homeColorPicker
      if (!p) return null
      return el.call(
        'homeWidgetAppColorOption',
        s.root,
        p.widgetId,
        p.appHue,
        el.context && el.context.designSystem
      )
    }
  },

  position: 'fixed',
  inset: '0',
  // Above the shell's DropdownRoot layer, in the same band ContextDropdown
  // uses (app.js: DropdownRoot 10010, ContextDropdown 10100, ModalRoot
  // 10500). Flat, not a `style` bag: this one is a CONSTANT, and only
  // ContextDropdown's dynamic per-open `z` needs the reactive style channel.
  zIndex: '10150',
  // FA515 — the `s.root &&` guard short-circuits before the key is read on a
  // run with no root, so the signal is declared explicitly rather than left
  // to the auto-tracker: a missed subscription here means the palette never
  // appears at all.
  hideDeps: ['_homeColorPicker'],
  hide: (el, s) => !(s.root && s.root._homeColorPicker),

  // Transparent backdrop — clicking (or right-clicking) anywhere off the
  // palette closes it. Sibling of the panel, so swatch clicks never hit it.
  HwcBackdrop: {
    position: 'absolute',
    inset: '0',
    onPointerdown: (e, el, s) => el.scope.closePicker(s),
    onContextmenu: (e, el, s) => {
      e.preventDefault()
      el.scope.closePicker(s)
    }
  },

  HwcPanel: {
    extends: 'MenuPanel',
    position: 'absolute',
    flow: 'y',
    gap: 'Z',
    padding: 'Z1',
    width: 'max-content',
    // Programmatic focus target only — kept out of normal Tab order, the
    // standard ARIA menu-container idiom (same as ContextDropdown's Menu).
    tabindex: '-1',
    role: 'menu',
    'aria-label': (el) => el.call('polyglot', 'workspace.widgetColor.pickerAria'),
    style: {
      left: (el, s) => el.scope.pickerPos(el).left + 'px',
      top: (el, s) => el.scope.pickerPos(el).top + 'px'
    },
    // subscribeTo + onUpdate is this app's established re-arm pattern
    // (CanvasPageSlot, GridContainer, ContextDropdown): onRender alone does
    // not reliably re-fire per open in v3.14.
    subscribeTo: ['_homeColorPicker'],
    onRender: (el) => el.scope.focusPicker(el),
    onUpdate: (el) => el.scope.focusPicker(el),
    onKeydown: (e, el, s) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      el.scope.closePicker(s)
    },

    // ── The module's OWN colour, offered first ────────────────────────────
    // An app-store / module widget's manifest hue stopped PAINTING by default
    // on 2026-09-02 (Nika: "pipeline still has color and set to no color also
    // does not change it. fix it fundamentally for also all app-store related
    // apps and widgets too"). Demoting it to a suggestion is what makes
    // "Default (no color)" genuinely neutralise every widget — but the app's
    // identity must not become unreachable, so it sits at the TOP of the
    // palette, one click away.
    //
    // Shaped like the "Default" row rather than dropped into the swatch grid:
    // the grid is a row of interchangeable suggestions, while this one is a
    // named, widget-specific choice that deserves its label on screen instead
    // of only in a tooltip. Hidden — and structurally inert — for every
    // widget with no manifest hue, which is the whole static plane.
    HwcApp: {
      hide: (el, s) => !el.scope.appOption(el, s),
      hideDeps: ['_homeColorPicker', '_homeWidgetColors'],
      extends: 'MenuPanelItem',
      tag: 'button',
      type: 'button',
      role: 'menuitemradio',
      flow: 'x',
      align: 'center left',
      gap: 'Z1',
      color: 'title',
      'aria-label': (el) => el.call('polyglot', 'workspace.widgetColor.appColor'),
      'aria-checked': (el, s) => ((el.scope.appOption(el, s) || {}).active ? 'true' : 'false'),
      HwcAppDot: {
        width: 'B',
        height: 'B',
        minWidth: 'B',
        round: 'radiusControl',
        align: 'center center',
        // The hue itself, resolved through the SAME `_rawHueEntry` the card
        // will use — the swatch can never preview a colour the card then
        // paints differently.
        background: (el, s) => (el.scope.appOption(el, s) || {}).bg || 'overlayStrong',
        color: (el, s) => (el.scope.appOption(el, s) || {}).ink || 'title',
        Icon: {
          icon: 'check',
          boxSize: 'Z1',
          display: (el, s) => ((el.scope.appOption(el, s) || {}).active ? 'inline-flex' : 'none')
        }
      },
      HwcAppLabel: {
        tag: 'span',
        flex: '1',
        whiteSpace: 'nowrap',
        text: '{{ workspace.widgetColor.appColor | polyglot }}'
      },
      onClick: (e, el, s) => {
        e.stopPropagation()
        const opt = el.scope.appOption(el, s)
        const picker = s.root && s.root._homeColorPicker
        const ops = (s.root && s.root._homeOps) || {}
        if (opt && picker && typeof ops.setWidgetColor === 'function') {
          // Stores the RAW hue, never a "use the manifest" sentinel: a widget
          // the user deliberately coloured must not silently repaint when the
          // module ships a new manifest.
          ops.setWidgetColor(picker.widgetId, opt.key)
        }
        el.scope.closePicker(s)
      }
    },

    HwcHeading: {
      tag: 'span',
      fontSize: 'Y',
      fontWeight: '700',
      letterSpacing: '0.04em',
      textTransform: 'uppercase',
      color: 'caption',
      padding: '0 Y',
      text: '{{ workspace.widgetColor.suggested | polyglot }}'
    },

    HwcGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(7, auto)',
      gap: 'Y',
      padding: '0 Y',
      childExtends: 'HomeWidgetColorSwatch',
      childrenAs: 'state',
      // FA515 — same reason as `hideDeps` above; plus `_homeWidgetColors`,
      // which `homeWidgetColorOptions` reads to flag the ACTIVE swatch, so
      // the tick moves when the pick changes while the palette is open.
      childrenDeps: ['_homeColorPicker', '_homeWidgetColors'],
      children: (el, s) =>
        el.call(
          'homeWidgetColorOptions',
          s.root,
          s.root && s.root._homeColorPicker ? s.root._homeColorPicker.widgetId : null
        )
    },

    // Clear back to the board's neutral solid. Reuses the house context-menu
    // row so this reads as one family with the menu it came from.
    HwcDefault: {
      extends: 'MenuPanelItem',
      tag: 'button',
      type: 'button',
      role: 'menuitem',
      flow: 'x',
      align: 'center left',
      gap: 'Z1',
      color: 'title',
      HwcDefaultDot: {
        // The neutral solid itself, drawn at swatch size so "Default" reads
        // as one more option in the same row of choices rather than as a
        // detached command.
        width: 'B',
        height: 'B',
        minWidth: 'B',
        round: 'radiusControl',
        background: 'overlayStrong',
        borderWidth: 'hairline',
        borderStyle: 'solid',
        borderColor: 'line'
      },
      HwcDefaultLabel: {
        tag: 'span',
        flex: '1',
        whiteSpace: 'nowrap',
        text: '{{ workspace.widgetColor.default | polyglot }}'
      },
      onClick: (e, el, s) => {
        e.stopPropagation()
        const picker = s.root && s.root._homeColorPicker
        const ops = (s.root && s.root._homeOps) || {}
        if (picker && typeof ops.setWidgetColor === 'function') {
          ops.setWidgetColor(picker.widgetId, null)
        }
        el.scope.closePicker(s)
      }
    }
  }
}

// One suggested colour. Rendered from a `homeWidgetColorOptions` row —
// `{ key, label, bg, ink, active }` — so the swatch paints the family's REAL
// background token (and therefore follows a branded workspace's poster-record
// override exactly like the card it will colour), never a copied literal.
export const HomeWidgetColorSwatch = {
  tag: 'button',
  type: 'button',
  role: 'menuitemradio',
  width: 'B',
  height: 'B',
  minWidth: 'B',
  round: 'radiusControl',
  padding: '0',
  border: 'none',
  cursor: 'pointer',
  align: 'center center',
  background: (el, s) => (s && s.bg) || 'overlayStrong',
  color: (el, s) => (s && s.ink) || 'title',
  transition: '120ms defaultBezier transform',
  ':hover': { transform: 'scale(1.12)' },
  ':active': { transform: 'scale(0.96)' },
  '@media (prefers-reduced-motion: reduce)': {
    ':hover': { transform: 'none' },
    ':active': { transform: 'none' }
  },
  title: (el, s) => (s && s.label) || '',
  aria: {
    label: (el, s) => (s && s.label) || '',
    checked: (el, s) => (s && s.active ? 'true' : 'false')
  },

  // The tick is painted in the family's OWN binary ink, which is the one ink
  // measured legible on that background (functions/homeWidgetHue.js) — a
  // fixed white or black tick would vanish on half the palette.
  Icon: {
    icon: 'check',
    boxSize: 'Z1',
    display: (el, s) => (s && s.active ? 'inline-flex' : 'none')
  },

  onClick: (e, el, s) => {
    e.stopPropagation()
    const picker = s.root && s.root._homeColorPicker
    const ops = (s.root && s.root._homeOps) || {}
    if (picker && typeof ops.setWidgetColor === 'function') {
      ops.setWidgetColor(picker.widgetId, s.key)
    }
    if (s.root) s.root.update({ _homeColorPicker: null }, { preventFetch: true })
  }
}
